import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  TextInput,
  Platform,
} from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { typography } from '../../theme/typography';
import { TimezoneService } from '../../services/timezone.service';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface TimezoneInputProps {
  value?: string;
  onChange?: (timezone: string, timezoneOffset: string) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outlined' | 'filled';
  state?: 'default' | 'error' | 'success' | 'warning';
  style?: any;
  containerStyle?: any;
}

// Common timezones grouped by region for better UX
const COMMON_TIMEZONES = [
  // Americas
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)', group: 'Americas' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)', group: 'Americas' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)', group: 'Americas' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)', group: 'Americas' },
  { value: 'America/Toronto', label: 'Toronto', group: 'Americas' },
  { value: 'America/Mexico_City', label: 'Mexico City', group: 'Americas' },
  { value: 'America/Sao_Paulo', label: 'São Paulo', group: 'Americas' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires', group: 'Americas' },
  
  // Europe
  { value: 'Europe/London', label: 'London', group: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris', group: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin', group: 'Europe' },
  { value: 'Europe/Rome', label: 'Rome', group: 'Europe' },
  { value: 'Europe/Madrid', label: 'Madrid', group: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam', group: 'Europe' },
  { value: 'Europe/Athens', label: 'Athens', group: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow', group: 'Europe' },
  
  // Middle East & Africa
  { value: 'Africa/Cairo', label: 'Cairo', group: 'Middle East & Africa' },
  { value: 'Asia/Dubai', label: 'Dubai', group: 'Middle East & Africa' },
  { value: 'Asia/Riyadh', label: 'Riyadh', group: 'Middle East & Africa' },
  { value: 'Asia/Jerusalem', label: 'Jerusalem', group: 'Middle East & Africa' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg', group: 'Middle East & Africa' },
  { value: 'Africa/Lagos', label: 'Lagos', group: 'Middle East & Africa' },
  
  // Asia
  { value: 'Asia/Shanghai', label: 'Shanghai', group: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Tokyo', group: 'Asia' },
  { value: 'Asia/Seoul', label: 'Seoul', group: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong', group: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore', group: 'Asia' },
  { value: 'Asia/Bangkok', label: 'Bangkok', group: 'Asia' },
  { value: 'Asia/Kolkata', label: 'Mumbai, New Delhi', group: 'Asia' },
  { value: 'Asia/Karachi', label: 'Karachi', group: 'Asia' },
  { value: 'Asia/Dhaka', label: 'Dhaka', group: 'Asia' },
  
  // Oceania
  { value: 'Australia/Sydney', label: 'Sydney', group: 'Oceania' },
  { value: 'Australia/Melbourne', label: 'Melbourne', group: 'Oceania' },
  { value: 'Pacific/Auckland', label: 'Auckland', group: 'Oceania' },
];

// Get all IANA timezones and format them
const getAllTimezones = (): Array<{ value: string; label: string; group: string }> => {
  try {
    const allTimezones = Intl.supportedValuesOf('timeZone');
    
    // Create a map of common timezones for quick lookup
    const commonMap = new Map(COMMON_TIMEZONES.map(tz => [tz.value, tz]));
    
    // Format all timezones
    const formatted = allTimezones.map(tz => {
      // If it's in common timezones, use that label
      if (commonMap.has(tz)) {
        return commonMap.get(tz)!;
      }
      
      // Otherwise, format the timezone name
      const parts = tz.split('/');
      const city = parts[parts.length - 1].replace(/_/g, ' ');
      const region = parts[0];
      
      return {
        value: tz,
        label: `${city} (${region})`,
        group: region,
      };
    });
    
    // Sort: common timezones first, then by region, then alphabetically
    const common = formatted.filter(tz => commonMap.has(tz.value));
    const others = formatted.filter(tz => !commonMap.has(tz.value));
    
    return [...common, ...others.sort((a, b) => a.label.localeCompare(b.label))];
  } catch (error) {
    console.error('Error getting timezones:', error);
    return COMMON_TIMEZONES;
  }
};

export const TimezoneInput: React.FC<TimezoneInputProps> = ({
  value = '',
  onChange,
  label,
  placeholder = 'Select timezone',
  error,
  helperText,
  disabled = false,
  required = false,
  size = 'md',
  variant = 'default',
  state = 'default',
  style,
  containerStyle,
}) => {
  const styles = useThemedStyles(getStyles);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [timezones, setTimezones] = useState<Array<{ value: string; label: string; group: string }>>([]);
  
  useEffect(() => {
    setTimezones(getAllTimezones());
  }, []);
  
  const filteredTimezones = timezones.filter(tz =>
    tz.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tz.value.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const selectedTimezone = timezones.find(tz => tz.value === value);
  const displayValue = selectedTimezone ? selectedTimezone.label : value || placeholder;
  
  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return { height: 40, fontSize: typography.fontSize.sm, paddingHorizontal: spacing.sm };
      case 'lg':
        return { height: 52, fontSize: typography.fontSize.base, paddingHorizontal: spacing.md };
      default:
        return { height: 48, fontSize: typography.fontSize.base, paddingHorizontal: spacing.md };
    }
  };
  
  const getVariantStyles = () => {
    switch (variant) {
      case 'outlined':
        return {
          borderWidth: 1,
          borderColor: error ? colors.error : state === 'focused' ? colors.primary : colors.border,
          backgroundColor: disabled ? colors.background.secondary : colors.white,
        };
      case 'filled':
        return {
          backgroundColor: disabled ? colors.background.secondary : colors.background.secondary,
          borderWidth: 0,
        };
      default:
        return {
          borderWidth: 1,
          borderBottomWidth: 2,
          borderColor: error ? colors.error : state === 'focused' ? colors.primary : colors.border,
          backgroundColor: disabled ? colors.background.secondary : 'transparent',
        };
    }
  };
  
  const handleSelectTimezone = (timezone: string) => {
    setIsPickerOpen(false);
    setSearchQuery('');
    
    // Calculate timezone offset
    try {
      const now = new Date();
      
      // Use Intl.DateTimeFormat to get the offset
      const formatter = new Intl.DateTimeFormat('en', {
        timeZone: timezone,
        timeZoneName: 'shortOffset',
      });
      
      const parts = formatter.formatToParts(now);
      const offsetPart = parts.find(part => part.type === 'timeZoneName');
      
      let formattedOffset = '+00:00';
      
      if (offsetPart?.value) {
        // Parse formats like "GMT+2", "GMT+02:00", "UTC+2", "GMT" (UTC), etc.
        const offsetStr = offsetPart.value;
        
        // Handle "GMT" or "UTC" without offset (means +00:00)
        if (offsetStr === 'GMT' || offsetStr === 'UTC') {
          formattedOffset = '+00:00';
        } else {
          // Match patterns like GMT+2, GMT+02, GMT+02:00, UTC+2:00, GMT-5
          const match = offsetStr.match(/([+-])(\d{1,2})(:?(\d{2}))?/);
          
          if (match) {
            const sign = match[1];
            const hours = match[2].padStart(2, '0');
            const minutes = match[4] || '00';
            formattedOffset = `${sign}${hours}:${minutes}`;
          } else {
            // Fallback: calculate offset by comparing UTC and timezone time
            try {
              const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
              const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
              const offsetMs = tzDate.getTime() - utcDate.getTime();
              const offsetMinutes = Math.round(offsetMs / 60000);
              const hours = Math.floor(Math.abs(offsetMinutes) / 60);
              const mins = Math.abs(offsetMinutes) % 60;
              const sign = offsetMinutes >= 0 ? '+' : '-';
              formattedOffset = `${sign}${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
            } catch (fallbackError) {
              console.error('Error in fallback offset calculation:', fallbackError);
              formattedOffset = '+00:00';
            }
          }
        }
      }
      
      console.log('[TimezoneInput] Selected timezone:', { timezone, offset: formattedOffset });
      onChange?.(timezone, formattedOffset);
    } catch (error) {
      console.error('Error calculating timezone offset:', error);
      // Fallback: try to get device timezone offset
      TimezoneService.getDeviceTimezone()
        .then(tzInfo => {
          onChange?.(timezone, tzInfo.offset);
        })
        .catch(() => {
          onChange?.(timezone, '+00:00');
        });
    }
  };
  
  const getCurrentState = () => {
    if (disabled) return 'disabled';
    if (error) return 'error';
    return state;
  };
  
  const currentState = getCurrentState();
  const sizeStyles = getSizeStyles();
  const variantStyles = getVariantStyles();
  
  return (
    <View style={[styles.container, containerStyle]}>
      {/* Label */}
      {label && (
        <Text style={[styles.label, error && styles.labelError]}>
          {label}
          {required && <Text style={styles.required}> *</Text>}
        </Text>
      )}
      
      {/* Input Container */}
      <TouchableOpacity
        style={[
          styles.inputContainer,
          sizeStyles,
          variantStyles,
          currentState === 'error' && styles.inputError,
          disabled && styles.inputDisabled,
          style,
        ]}
        onPress={() => !disabled && setIsPickerOpen(true)}
        disabled={disabled}
        activeOpacity={0.7}
      >
        <Text
          style={[
            styles.inputText,
            { fontSize: sizeStyles.fontSize },
            !value && styles.placeholderText,
            disabled && styles.disabledText,
          ]}
          numberOfLines={1}
        >
          {displayValue}
        </Text>
        <Ionicons
          name="chevron-down"
          size={20}
          color={disabled ? colors.text.secondary : colors.text.primary}
        />
      </TouchableOpacity>
      
      {/* Error/Helper Text */}
      {(error || helperText) && (
        <Text style={[styles.helperText, error && styles.errorText]}>
          {error || helperText}
        </Text>
      )}
      
      {/* Timezone Picker Modal */}
      <Modal
        visible={isPickerOpen}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setIsPickerOpen(false)}
        presentationStyle={Platform.OS === 'ios' ? 'pageSheet' : undefined}
      >
        {Platform.OS === 'ios' ? (
          <View style={styles.modalContainer}>
            {/* Modal Header */}
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Timezone</Text>
              <TouchableOpacity
                onPress={() => {
                  setIsPickerOpen(false);
                  setSearchQuery('');
                }}
                style={styles.closeButton}
              >
                <Ionicons name="close" size={24} color={colors.text.primary} />
              </TouchableOpacity>
            </View>
            
            {/* Search Input */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={colors.text.secondary} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search timezones..."
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholderTextColor={colors.text.secondary}
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity onPress={() => setSearchQuery('')} style={styles.clearButton}>
                  <Ionicons name="close-circle" size={20} color={colors.text.secondary} />
                </TouchableOpacity>
              )}
            </View>
            
            {/* Timezone List */}
            <FlatList
              data={filteredTimezones}
              keyExtractor={(item) => item.value}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[
                    styles.timezoneItem,
                    value === item.value && styles.timezoneItemSelected,
                  ]}
                  onPress={() => handleSelectTimezone(item.value)}
                  activeOpacity={0.7}
                >
                  <View style={styles.timezoneItemContent}>
                    <Text
                      style={[
                        styles.timezoneLabel,
                        value === item.value && styles.timezoneLabelSelected,
                      ]}
                    >
                      {item.label}
                    </Text>
                    <Text style={styles.timezoneValue}>{item.value}</Text>
                  </View>
                  {value === item.value && (
                    <Ionicons name="checkmark" size={20} color={colors.primary} />
                  )}
                </TouchableOpacity>
              )}
              style={styles.timezoneList}
              contentContainerStyle={styles.timezoneListContent}
            />
          </View>
        ) : (
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              {/* Modal Header */}
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Select Timezone</Text>
                <TouchableOpacity
                  onPress={() => {
                    setIsPickerOpen(false);
                    setSearchQuery('');
                  }}
                  style={styles.closeButton}
                >
                  <Ionicons name="close" size={24} color={colors.text.primary} />
                </TouchableOpacity>
              </View>
              
              {/* Search Input */}
              <View style={styles.searchContainer}>
                <Ionicons name="search" size={20} color={colors.text.secondary} style={styles.searchIcon} />
                <TextInput
                  style={styles.searchInput}
                  placeholder="Search timezones..."
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  placeholderTextColor={colors.text.secondary}
                />
                {searchQuery.length > 0 && (
                  <TouchableOpacity onPress={() => setSearchQuery('')} style={styles.clearButton}>
                    <Ionicons name="close-circle" size={20} color={colors.text.secondary} />
                  </TouchableOpacity>
                )}
              </View>
              
              {/* Timezone List */}
              <FlatList
                data={filteredTimezones}
                keyExtractor={(item) => item.value}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={[
                      styles.timezoneItem,
                      value === item.value && styles.timezoneItemSelected,
                    ]}
                    onPress={() => handleSelectTimezone(item.value)}
                    activeOpacity={0.7}
                  >
                    <View style={styles.timezoneItemContent}>
                      <Text
                        style={[
                          styles.timezoneLabel,
                          value === item.value && styles.timezoneLabelSelected,
                        ]}
                      >
                        {item.label}
                      </Text>
                      <Text style={styles.timezoneValue}>{item.value}</Text>
                    </View>
                    {value === item.value && (
                      <Ionicons name="checkmark" size={20} color={colors.primary} />
                    )}
                  </TouchableOpacity>
                )}
                style={styles.timezoneList}
                contentContainerStyle={styles.timezoneListContent}
              />
            </View>
          </View>
        )}
      </Modal>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    marginBottom: spacing.md,
  },
  label: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  labelError: {
    color: colors.error,
  },
  required: {
    color: colors.error,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 8,
    paddingHorizontal: spacing.md,
  },
  inputText: {
    flex: 1,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary,
  },
  placeholderText: {
    color: colors.text.secondary,
  },
  disabledText: {
    color: colors.text.secondary,
  },
  inputError: {
    borderColor: colors.error,
  },
  inputDisabled: {
    opacity: 0.6,
  },
  helperText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginTop: spacing.xs,
  },
  errorText: {
    color: colors.error,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: colors.background.surface,
    paddingTop: Platform.OS === 'ios' ? 50 : 20,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
    paddingBottom: Platform.OS === 'ios' ? 34 : spacing.lg,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  modalTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  closeButton: {
    padding: spacing.xs,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: spacing.lg,
    marginVertical: spacing.md,
    paddingHorizontal: spacing.md,
    backgroundColor: colors.background.secondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  searchIcon: {
    marginRight: spacing.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary,
    paddingVertical: spacing.sm,
  },
  clearButton: {
    padding: spacing.xs,
  },
  timezoneList: {
    flex: 1,
  },
  timezoneListContent: {
    paddingBottom: spacing.md,
  },
  timezoneItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  timezoneItemSelected: {
    backgroundColor: colors.background.secondary,
  },
  timezoneItemContent: {
    flex: 1,
  },
  timezoneLabel: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  timezoneLabelSelected: {
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  timezoneValue: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
  },
});

